import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { fetchCategories, fetchProductsPage } from '../utils/categories';
import ProductCard from './ProductCard';
import BackButton from './BackButton';
import Pagination from './Pagination';
import './ProductCategoryPage.css';

const PAGE_SIZE = 15;

const ProductCategoryPage = ({ categoryId = 1, highlightProductId = null, onCartUpdate, onBackClick }) => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const pageFromUrl = Math.max(1, parseInt(searchParams.get('page') || '1', 10) || 1);
  const prevCategoryRef = useRef(categoryId);

  const [products, setProducts] = useState([]);
  const [categoryName, setCategoryName] = useState('Products');
  const [categoryProductCount, setCategoryProductCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [totalProducts, setTotalProducts] = useState(0);
  const [totalPages, setTotalPages] = useState(1);

  const setPageInUrl = useCallback(
    (nextPage) => {
      const clamped = Math.max(1, nextPage);
      setSearchParams(
        clamped > 1 ? { page: String(clamped) } : {},
        { replace: true }
      );
    },
    [setSearchParams]
  );

  // Reset to page 1 only when switching to a different category
  useEffect(() => {
    if (prevCategoryRef.current !== categoryId) {
      prevCategoryRef.current = categoryId;
      setSearchParams({}, { replace: true });
    }
  }, [categoryId, setSearchParams]);

  useEffect(() => {
    let cancelled = false;

    fetchCategories()
      .then((rows) => {
        if (cancelled) return;
        const cat = rows.find((c) => c.id === Number(categoryId));
        setCategoryName(cat ? cat.name : 'Products');
        setCategoryProductCount(cat?.productCount || 0);
      })
      .catch(() => {
        if (!cancelled) {
          setCategoryName('Products');
          setCategoryProductCount(0);
        }
      });

    return () => {
      cancelled = true;
    };
  }, [categoryId]);

  useEffect(() => {
    let cancelled = false;

    const load = async () => {
      setLoading(true);
      try {
        const result = await fetchProductsPage({
          categoryId,
          page: pageFromUrl,
          pageSize: PAGE_SIZE,
          totalHint: categoryProductCount,
        });

        if (cancelled) return;

        setProducts(result.products);
        setTotalProducts(result.total);
        setTotalPages(result.totalPages);

        if (pageFromUrl > result.totalPages && result.totalPages > 0) {
          setPageInUrl(result.totalPages);
        }
      } catch (err) {
        console.error('Error fetching products:', err);
        if (!cancelled) {
          setProducts([]);
          setTotalProducts(0);
          setTotalPages(1);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    load();
    return () => {
      cancelled = true;
    };
  }, [categoryId, pageFromUrl, categoryProductCount, setPageInUrl]);

  const safePage = Math.min(pageFromUrl, totalPages);
  const start = totalProducts > 0 ? (safePage - 1) * PAGE_SIZE + 1 : 0;
  const end = Math.min(safePage * PAGE_SIZE, totalProducts);
  const showPagination = totalPages > 1;

  const handlePageChange = (nextPage) => {
    const clamped = Math.max(1, Math.min(nextPage, totalPages));
    setPageInUrl(clamped);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleProductClick = (product) => {
    const params = new URLSearchParams({
      fromCategory: String(categoryId),
      fromPage: String(safePage),
    });
    navigate(`/product/${product.id}?${params.toString()}`, {
      state: {
        fromCategory: Number(categoryId),
        fromPage: safePage,
        categoryName,
      },
    });
  };

  return (
    <div className="product-category-page">
      {onBackClick && <BackButton onClick={onBackClick} />}
      <div className="product-list-section">
        <div className="page-heading">
          <h2>{categoryName} Products</h2>
          <p>
            Trusted pharmacy essentials with fast delivery and reliable care.
            {!loading && totalProducts > 0 && (
              <span className="product-count"> · {totalProducts} products</span>
            )}
          </p>
        </div>
        {loading ? (
          <div className="loading">Loading products...</div>
        ) : (
          <>
            <div className="product-grid">
              {products.length > 0 ? (
                products.map((product) => (
                  <ProductCard
                    key={product.id}
                    product={product}
                    onCartUpdate={onCartUpdate}
                    onProductClick={handleProductClick}
                    highlight={highlightProductId === product.id}
                  />
                ))
              ) : (
                <div className="no-products">No products available in this category</div>
              )}
            </div>
            {showPagination && (
              <div className="pagination-wrap">
                <p className="pagination-info">
                  Showing {start}–{end} of {totalProducts}
                </p>
                <Pagination page={safePage} totalPages={totalPages} onPageChange={handlePageChange} />
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default ProductCategoryPage;
